import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { apiClient } from '../../utils/apiClient';
import styled, { keyframes } from 'styled-components';
import { FiExternalLink, FiClock, FiRss, FiRefreshCw, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import RapaportDetailModal from '../../components/RapaportDetailModal';
import { useUser } from '../../stores/authStore';
// Animations
const fadeUp = keyframes `
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;
const spin = keyframes `
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const shimmer = keyframes `
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;
// Styled Components
const PageWrapper = styled.div `
  min-height: 100vh;
  background: var(--bg-primary);
`;
const HeroSection = styled.div `
  padding: 48px 24px 32px;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  animation: ${fadeUp} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 32px 16px 24px;
  }
`;
const HeroTitle = styled.h1 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
  letter-spacing: 0.02em;
`;
const HeroAccent = styled.span `
  color: var(--accent-gold, #B8860B);
`;
const HeroSubtitle = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  color: var(--text-secondary);
  max-width: 540px;
  margin: 0 auto;
  line-height: 1.6;
`;
const ContentWrapper = styled.div `
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px 64px;

  @media (max-width: 768px) {
    padding: 24px 16px 48px;
  }
`;
// Curated Sources
const SourcesSection = styled.div `
  margin-bottom: 36px;
  animation: ${fadeUp} 0.6s ease-out 0.1s backwards;
`;
const SectionLabel = styled.h2 `
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--text-tertiary);
  margin-bottom: 16px;
`;
const SourcesGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const SourceCard = styled.a `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 16px 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  text-decoration: none;
  transition: all 0.25s ease;
  text-align: center;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;
const SourceName = styled.span `
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
`;
const SourceDesc = styled.span `
  font-family: 'DM Sans', sans-serif;
  font-size: 11px;
  color: var(--text-tertiary);
  line-height: 1.3;
`;
// Category Tabs
const FilterBar = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
  overflow-x: auto;
  padding-bottom: 4px;
  animation: ${fadeUp} 0.6s ease-out 0.2s backwards;

  &::-webkit-scrollbar {
    height: 2px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
  }
`;
const FilterTab = styled.button `
  padding: 8px 18px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  border: 1px solid ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'var(--border-color)'};
  background: ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.02em;

  &:hover {
    border-color: var(--accent-gold, #B8860B);
    color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  }
`;
// Articles Grid
const ArticlesGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 20px;
  animation: ${fadeUp} 0.6s ease-out 0.3s backwards;

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;
const ArticleCard = styled.a `
  display: flex;
  flex-direction: column;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  text-decoration: none;
  transition: all 0.25s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: var(--border-color-hover);
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--accent-gold, #B8860B);
    transition: width 0.3s ease;
    z-index: 1;
  }

  &:hover::before {
    width: 100%;
  }
`;
const ArticleImageWrapper = styled.div `
  width: 100%;
  height: 180px;
  overflow: hidden;
  background: var(--bg-tertiary);
  flex-shrink: 0;
`;
const ArticleImage = styled.img `
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;

  ${ArticleCard}:hover & {
    transform: scale(1.04);
  }
`;
const ArticleBody = styled.div `
  display: flex;
  flex-direction: column;
  padding: 20px 24px 24px;
  flex: 1;
`;
const ArticleMeta = styled.div `
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`;
const CategoryBadge = styled.span `
  display: inline-block;
  padding: 3px 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  border: 1px solid var(--accent-gold, #B8860B);
  color: var(--accent-gold, #B8860B);
`;
const ArticleSource = styled.span `
  font-family: 'DM Sans', sans-serif;
  font-size: 11px;
  color: var(--text-tertiary);
  font-weight: 500;
`;
const ArticleTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 17px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const ArticleDescription = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 14px;
`;
const ArticleFooter = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
`;
const ArticleDate = styled.span `
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: 'DM Sans', sans-serif;
  font-size: 11px;
  color: var(--text-tertiary);

  svg {
    width: 12px;
    height: 12px;
  }
`;
const ReadMore = styled.span `
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--accent-gold, #B8860B);

  svg {
    width: 12px;
    height: 12px;
    transition: transform 0.2s ease;
  }

  ${ArticleCard}:hover & svg {
    transform: translate(2px, -2px);
  }
`;
// Pagination
const PaginationWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 40px;
  animation: ${fadeUp} 0.4s ease-out;
`;
const PageButton = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'var(--border-color)'};
  background: ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: var(--accent-gold, #B8860B);
    color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;
const PageInfo = styled.span `
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: var(--text-tertiary);
  margin: 0 8px;
`;
// States
const EmptyState = styled.div `
  text-align: center;
  padding: 60px 24px;
  animation: ${fadeUp} 0.5s ease-out;
`;
const EmptyIcon = styled.div `
  font-size: 40px;
  margin-bottom: 16px;
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 40px;
    height: 40px;
  }
`;
const EmptyTitle = styled.h3 `
  font-family: 'Playfair Display', 'Adamina', serif;
  font-size: 20px;
  color: var(--text-primary);
  margin-bottom: 8px;
`;
const EmptyText = styled.p `
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  color: var(--text-secondary);
`;
const RefreshButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 16px;
  padding: 10px 20px;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  background: transparent;
  border: 1px solid var(--accent-gold, #B8860B);
  color: var(--accent-gold, #B8860B);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--accent-gold, #B8860B);
    color: white;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;
const SkeletonCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  overflow: hidden;
`;
const SkeletonImage = styled.div `
  width: 100%;
  height: 180px;
  background: linear-gradient(90deg, var(--bg-tertiary) 25%, var(--border-color) 50%, var(--bg-tertiary) 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;
const SkeletonBody = styled.div `
  padding: 20px 24px 24px;
`;
const SkeletonLine = styled.div `
  width: ${props => props.$width || '100%'};
  height: ${props => props.$height || '14px'};
  background: linear-gradient(90deg, var(--bg-tertiary) 25%, var(--border-color) 50%, var(--bg-tertiary) 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;
// Rapaport Tab Styled Components
const TabBar = styled.div `
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--border-color);
  animation: ${fadeUp} 0.4s ease-out;
`;
const Tab = styled.button `
  padding: 12px 24px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? 'var(--accent-gold, #B8860B)' : 'transparent'};
  color: ${props => props.$active ? 'var(--text-primary)' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--text-primary);
  }
`;
const RapaportControls = styled.div `
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
  padding: 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  animation: ${fadeUp} 0.4s ease-out;
`;
const SearchInput = styled.input `
  flex: 1;
  min-width: 200px;
  padding: 10px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
  }
`;
const DateInput = styled.input `
  padding: 10px 16px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-gold, #B8860B);
  }
`;
const ActionButton = styled.button `
  padding: 10px 20px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid ${props => props.$primary ? 'var(--accent-gold, #B8860B)' : 'var(--border-color)'};
  background: ${props => props.$primary ? 'var(--accent-gold, #B8860B)' : 'transparent'};
  color: ${props => props.$primary ? 'white' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.$primary ? 'var(--accent-gold-dark, #9a6f0a)' : 'var(--bg-tertiary)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
// Helper
const CATEGORY_LABELS = {
    industry: 'Diamond Industry',
    market: 'Market & Prices',
    jewelry: 'Jewelry Trade',
    'lab-grown': 'Lab-Grown',
    natural: 'Natural Diamonds',
};
function getRelativeTime(dateStr) {
    try {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        const diffHr = Math.floor(diffMs / 3600000);
        const diffDay = Math.floor(diffMs / 86400000);
        if (diffMin < 1)
            return 'Just now';
        if (diffMin < 60)
            return `${diffMin}m ago`;
        if (diffHr < 24)
            return `${diffHr}h ago`;
        if (diffDay < 7)
            return `${diffDay}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    catch {
        return '';
    }
}
const News = () => {
    const [articles, setArticles] = useState([]);
    const [sources, setSources] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState('all');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalArticles, setTotalArticles] = useState(0);
    // Rapaport state
    const user = useUser();
    const isAdmin = ['PLATFORM_ADMIN', 'SUPER_ADMIN'].includes(user?.role || '');
    const [activeTab, setActiveTab] = useState(isAdmin ? 'rapaport' : 'news');
    const [rapaportComments, setRapaportComments] = useState([]);
    const [isRefreshingRapaport, setIsRefreshingRapaport] = useState(false);
    const [rapaportSearch, setRapaportSearch] = useState('');
    const [rapaportDateRange, setRapaportDateRange] = useState({ start: '', end: '' });
    const [rapaportPage, setRapaportPage] = useState(1);
    const [rapaportTotalPages, setRapaportTotalPages] = useState(1);
    const [selectedComment, setSelectedComment] = useState(null);
    const [isLoadingRapaport, setIsLoadingRapaport] = useState(false);
    const fetchNews = async (category, page = 1) => {
        setIsLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (category && category !== 'all')
                params.set('category', category);
            params.set('page', page.toString());
            params.set('pageSize', '10');
            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await apiClient.get(`news-feed${query}`);
            if (response.ok && response.data) {
                const data = response.data;
                setArticles(data.articles || []);
                setSources(data.sources || []);
                setCategories(data.categories || []);
                if (data.pagination) {
                    setTotalPages(data.pagination.totalPages);
                    setTotalArticles(data.pagination.totalArticles);
                }
            }
            else {
                setError('Unable to load news at this time.');
            }
        }
        catch {
            setError('Unable to load news at this time.');
        }
        finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetchNews();
    }, []);
    const handleCategoryChange = (categoryId) => {
        setActiveCategory(categoryId);
        setCurrentPage(1);
        fetchNews(categoryId, 1);
    };
    const handleRefresh = () => {
        fetchNews(activeCategory, currentPage);
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchNews(activeCategory, page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    // Rapaport functions
    const fetchRapaportComments = async (page = 1) => {
        setIsLoadingRapaport(true);
        setError('');
        try {
            const params = new URLSearchParams();
            params.set('page', page.toString());
            params.set('pageSize', '10');
            if (rapaportSearch)
                params.set('search', rapaportSearch);
            if (rapaportDateRange.start)
                params.set('startDate', rapaportDateRange.start);
            if (rapaportDateRange.end)
                params.set('endDate', rapaportDateRange.end);
            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await apiClient.get(`rapaport-feed${query}`);
            if (response.ok && response.data) {
                const data = response.data;
                setRapaportComments(data.comments || []);
                if (data.pagination) {
                    setRapaportTotalPages(data.pagination.totalPages);
                }
            }
            else {
                setError('Unable to load Rapaport comments.');
            }
        }
        catch {
            setError('Unable to load Rapaport comments.');
        }
        finally {
            setIsLoadingRapaport(false);
        }
    };
    const handleRefreshRapaport = async () => {
        setIsRefreshingRapaport(true);
        try {
            const response = await apiClient.post('rapaport-scraper', {});
            if (response.ok && response.data) {
                const data = response.data;
                alert(`${data.message || 'Refresh complete'}\n\nNew: ${data.stats?.newComments || 0}, Updated: ${data.stats?.updatedComments || 0}`);
                await fetchRapaportComments(rapaportPage);
            }
            else {
                alert('Failed to refresh Rapaport data');
            }
        }
        catch {
            alert('Error refreshing Rapaport data');
        }
        finally {
            setIsRefreshingRapaport(false);
        }
    };
    const handleRapaportSearch = () => {
        setRapaportPage(1);
        fetchRapaportComments(1);
    };
    const handleRapaportPageChange = (page) => {
        setRapaportPage(page);
        fetchRapaportComments(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const handleCommentClick = async (commentId) => {
        try {
            const response = await apiClient.get(`rapaport-feed/${commentId}`);
            if (response.ok && response.data) {
                const data = response.data;
                if (data.comment) {
                    setSelectedComment(data.comment);
                }
            }
        }
        catch (err) {
            console.error('Failed to load comment details:', err);
        }
    };
    useEffect(() => {
        if (isAdmin && activeTab === 'rapaport' && rapaportComments.length === 0) {
            fetchRapaportComments();
        }
    }, [activeTab]);
    // Fallback sources if API hasn't loaded yet
    const displaySources = sources.length > 0 ? sources : [
        { name: 'Rapaport', url: 'https://www.rapaport.com', description: 'Diamond price lists & market analysis' },
        { name: 'National Jeweler', url: 'https://www.nationaljeweler.com', description: 'Jewelry industry news & trends' },
        { name: 'JCK', url: 'https://www.jckonline.com', description: 'Jewelry trade publication' },
        { name: 'Natural Diamonds', url: 'https://www.naturaldiamonds.com', description: 'Natural Diamond Council news' },
        { name: 'IDEX Online', url: 'https://www.idexonline.com', description: 'Diamond exchange & pricing' },
    ];
    const displayCategories = categories.length > 0 ? categories : [
        { id: 'all', label: 'All' },
        { id: 'industry', label: 'Diamond Industry' },
        { id: 'market', label: 'Market & Prices' },
        { id: 'jewelry', label: 'Jewelry Trade' },
        { id: 'lab-grown', label: 'Lab-Grown' },
        { id: 'natural', label: 'Natural Diamonds' },
    ];
    // Build page numbers to show
    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        const end = Math.min(totalPages, start + maxVisible - 1);
        start = Math.max(1, end - maxVisible + 1);
        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };
    return (_jsxs(PageWrapper, { children: [_jsxs(HeroSection, { children: [_jsxs(HeroTitle, { children: ["Industry ", _jsx(HeroAccent, { children: "News" })] }), _jsx(HeroSubtitle, { children: "Stay informed with the latest developments in the diamond and jewelry industry" })] }), _jsxs(ContentWrapper, { children: [isAdmin && (_jsxs(TabBar, { children: [_jsx(Tab, { "$active": activeTab === 'news', onClick: () => setActiveTab('news'), children: "Generic News" }), _jsx(Tab, { "$active": activeTab === 'rapaport', onClick: () => setActiveTab('rapaport'), children: "Rapaport Market Comments" })] })), activeTab === 'news' && (_jsxs(_Fragment, { children: [_jsxs(SourcesSection, { children: [_jsx(SectionLabel, { children: "Curated Sources" }), _jsx(SourcesGrid, { children: displaySources.map((source) => (_jsxs(SourceCard, { href: source.url, target: "_blank", rel: "noopener noreferrer", children: [_jsx(SourceName, { children: source.name }), _jsx(SourceDesc, { children: source.description })] }, source.name))) })] }), _jsx(FilterBar, { children: displayCategories.map((cat) => (_jsx(FilterTab, { "$active": activeCategory === cat.id, onClick: () => handleCategoryChange(cat.id), children: cat.label }, cat.id))) }), isLoading && (_jsx(ArticlesGrid, { children: Array.from({ length: 6 }).map((_, i) => (_jsxs(SkeletonCard, { children: [_jsx(SkeletonImage, {}), _jsxs(SkeletonBody, { children: [_jsx(SkeletonLine, { "$width": "40%", "$height": "10px" }), _jsx(SkeletonLine, { "$width": "90%", "$height": "18px" }), _jsx(SkeletonLine, { "$width": "75%", "$height": "18px" }), _jsx(SkeletonLine, { "$width": "100%", "$height": "12px" }), _jsx(SkeletonLine, { "$width": "50%", "$height": "10px" })] })] }, i))) })), !isLoading && error && articles.length === 0 && (_jsxs(EmptyState, { children: [_jsx(EmptyIcon, { children: _jsx(FiRss, {}) }), _jsx(EmptyTitle, { children: "News Unavailable" }), _jsx(EmptyText, { children: error }), _jsx(EmptyText, { children: "Visit our curated sources above for the latest industry news." }), _jsxs(RefreshButton, { onClick: handleRefresh, children: [_jsx(FiRefreshCw, {}), " Try Again"] })] })), !isLoading && !error && articles.length === 0 && (_jsxs(EmptyState, { children: [_jsx(EmptyIcon, { children: _jsx(FiRss, {}) }), _jsx(EmptyTitle, { children: "No Articles Found" }), _jsx(EmptyText, { children: "No news articles matched this category. Try selecting a different filter." }), _jsxs(RefreshButton, { onClick: handleRefresh, children: [_jsx(FiRefreshCw, {}), " Refresh"] })] })), !isLoading && articles.length > 0 && (_jsxs(_Fragment, { children: [_jsx(ArticlesGrid, { children: articles.map((article, index) => (_jsxs(ArticleCard, { href: article.link, target: "_blank", rel: "noopener noreferrer", children: [article.imageUrl && (_jsx(ArticleImageWrapper, { children: _jsx(ArticleImage, { src: article.imageUrl, alt: "", loading: "lazy", onError: (e) => {
                                                            // Hide broken images
                                                            e.target.parentElement.style.display = 'none';
                                                        } }) })), _jsxs(ArticleBody, { children: [_jsxs(ArticleMeta, { children: [_jsx(CategoryBadge, { children: CATEGORY_LABELS[article.category] || article.category }), _jsx(ArticleSource, { children: article.source })] }), _jsx(ArticleTitle, { children: article.title }), article.description && (_jsx(ArticleDescription, { children: article.description })), _jsxs(ArticleFooter, { children: [_jsxs(ArticleDate, { children: [_jsx(FiClock, {}), getRelativeTime(article.pubDate)] }), _jsxs(ReadMore, { children: ["Read ", _jsx(FiExternalLink, {})] })] })] })] }, `${article.title}-${index}`))) }), totalPages > 1 && (_jsxs(PaginationWrapper, { children: [_jsx(PageButton, { onClick: () => handlePageChange(currentPage - 1), disabled: currentPage === 1, title: "Previous page", children: _jsx(FiChevronLeft, {}) }), getPageNumbers().map((page) => (_jsx(PageButton, { "$active": currentPage === page, onClick: () => handlePageChange(page), children: page }, page))), _jsxs(PageInfo, { children: ["of ", totalPages] }), _jsx(PageButton, { onClick: () => handlePageChange(currentPage + 1), disabled: currentPage === totalPages, title: "Next page", children: _jsx(FiChevronRight, {}) })] }))] }))] })), activeTab === 'rapaport' && isAdmin && (_jsxs(_Fragment, { children: [_jsxs(RapaportControls, { children: [_jsx(SearchInput, { type: "text", placeholder: "Search market comments...", value: rapaportSearch, onChange: (e) => setRapaportSearch(e.target.value), onKeyDown: (e) => e.key === 'Enter' && handleRapaportSearch() }), _jsx(DateInput, { type: "date", value: rapaportDateRange.start, onChange: (e) => setRapaportDateRange({ ...rapaportDateRange, start: e.target.value }) }), _jsx(DateInput, { type: "date", value: rapaportDateRange.end, onChange: (e) => setRapaportDateRange({ ...rapaportDateRange, end: e.target.value }) }), _jsx(ActionButton, { onClick: handleRapaportSearch, children: "Search" }), _jsx(ActionButton, { "$primary": true, onClick: handleRefreshRapaport, disabled: isRefreshingRapaport, children: isRefreshingRapaport ? 'Refreshing...' : 'Refresh Data' })] }), isLoadingRapaport && (_jsx(ArticlesGrid, { children: Array.from({ length: 6 }).map((_, i) => (_jsxs(SkeletonCard, { children: [_jsx(SkeletonImage, {}), _jsxs(SkeletonBody, { children: [_jsx(SkeletonLine, { "$width": "40%", "$height": "10px" }), _jsx(SkeletonLine, { "$width": "90%", "$height": "18px" }), _jsx(SkeletonLine, { "$width": "75%", "$height": "18px" }), _jsx(SkeletonLine, { "$width": "100%", "$height": "12px" }), _jsx(SkeletonLine, { "$width": "50%", "$height": "10px" })] })] }, i))) })), !isLoadingRapaport && error && rapaportComments.length === 0 && (_jsxs(EmptyState, { children: [_jsx(EmptyIcon, { children: _jsx(FiRss, {}) }), _jsx(EmptyTitle, { children: "Comments Unavailable" }), _jsx(EmptyText, { children: error }), _jsxs(RefreshButton, { onClick: () => fetchRapaportComments(rapaportPage), children: [_jsx(FiRefreshCw, {}), " Try Again"] })] })), !isLoadingRapaport && !error && rapaportComments.length === 0 && (_jsxs(EmptyState, { children: [_jsx(EmptyIcon, { children: _jsx(FiRss, {}) }), _jsx(EmptyTitle, { children: "No Comments Found" }), _jsx(EmptyText, { children: "No Rapaport market comments available. Try refreshing the data." }), _jsxs(RefreshButton, { onClick: handleRefreshRapaport, disabled: isRefreshingRapaport, children: [_jsx(FiRefreshCw, {}), " ", isRefreshingRapaport ? 'Refreshing...' : 'Refresh Data'] })] })), !isLoadingRapaport && rapaportComments.length > 0 && (_jsxs(_Fragment, { children: [_jsx(ArticlesGrid, { children: rapaportComments.map((comment) => (_jsxs(ArticleCard, { as: "div", onClick: () => handleCommentClick(comment.id), style: { cursor: 'pointer' }, children: [comment.imageUrl && (_jsx(ArticleImageWrapper, { children: _jsx(ArticleImage, { src: comment.imageUrl, alt: comment.title, loading: "lazy" }) })), _jsxs(ArticleBody, { children: [_jsx(ArticleMeta, { children: _jsx(CategoryBadge, { children: "Market Comment" }) }), _jsx(ArticleTitle, { children: comment.title }), comment.preview && (_jsx(ArticleDescription, { children: comment.preview })), _jsxs(ArticleFooter, { children: [_jsxs(ArticleDate, { children: [_jsx(FiClock, {}), new Date(comment.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })] }), _jsxs(ReadMore, { children: ["Read ", _jsx(FiExternalLink, {})] })] })] })] }, comment.id))) }), rapaportTotalPages > 1 && (_jsxs(PaginationWrapper, { children: [_jsx(PageButton, { onClick: () => handleRapaportPageChange(rapaportPage - 1), disabled: rapaportPage === 1, title: "Previous page", children: _jsx(FiChevronLeft, {}) }), Array.from({ length: Math.min(5, rapaportTotalPages) }, (_, i) => {
                                                const pageNum = i + 1;
                                                return (_jsx(PageButton, { "$active": rapaportPage === pageNum, onClick: () => handleRapaportPageChange(pageNum), children: pageNum }, pageNum));
                                            }), _jsxs(PageInfo, { children: ["of ", rapaportTotalPages] }), _jsx(PageButton, { onClick: () => handleRapaportPageChange(rapaportPage + 1), disabled: rapaportPage === rapaportTotalPages, title: "Next page", children: _jsx(FiChevronRight, {}) })] }))] }))] })), selectedComment && (_jsx(RapaportDetailModal, { comment: selectedComment, onClose: () => setSelectedComment(null) }))] })] }));
};
export default News;
